import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { InlinePlacementActions } from 'constants/inlinePlacement';
import { BettingTypes } from 'constants/markets';
import {
  PlacementErrorsIds,
  VALIDATION_ERROR_BET_OUTDATED_LINE,
  VALIDATION_ERROR_BET_OUTDATED_ODDS
} from 'constants/placement';
import { useMarketUnits } from 'hooks/useMarketUnits';
import { usePlacementData } from 'hooks/usePlacement';
import useTakeOdds from 'hooks/useTakeOdds';
import {
  getBalanceWsEnabled,
  getGeneralWsEnabled,
  getIsOperatorBalanceEnabled,
  getPNCEnabledSetting
} from 'redux/modules/appConfigs/selectors';
import { setRGErrorMessage } from 'redux/modules/betslip';
import { TCurrentBet } from 'redux/modules/currentBets/type';
import { failureInlinePlacedBet, successInlinePlacedBet, updateInlineSelectedBet } from 'redux/modules/inlinePlacement';
import { TInlineSelectedBet } from 'redux/modules/inlinePlacement/type';
import { removePlacedSelectedBetError } from 'redux/modules/placement';
import { getMarketDataById } from 'redux/modules/placement/selectors';
import { TPlacementError } from 'redux/modules/placement/type';
import { fetchBalance } from 'redux/modules/user';
import { hideInlinePlacementWhatIf } from 'redux/modules/whatIf';
import { InlinePlacementAction } from 'types/inlinePlacement';
import { isResponsibleGamblingError } from 'utils/betslip';
import { getBetIdentityData } from 'utils/inlinePlacement';

const InlinePlacementInjectionItem = ({ bet }: { bet: TInlineSelectedBet }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isOperatorBalanceEnabled = useSelector(getIsOperatorBalanceEnabled);
  const balanceWsEnabled = useSelector(getBalanceWsEnabled);
  const generalWsEnabled = useSelector(getGeneralWsEnabled);
  const market = useSelector(getMarketDataById(bet.pageBlock, bet.marketId, bet.sportId));
  const isPNCEnabled = useSelector(getPNCEnabledSetting);

  const marketUnitTranslated = useMarketUnits((bet?.marketUnit || bet?.lineRangeInfo?.marketUnit) ?? 'points');

  const isLineMarket = bet.bettingType === BettingTypes.LINE || market?.description?.bettingType === BettingTypes.LINE;

  const { takeOdds } = useTakeOdds({ ...bet, betType: bet.type, size: bet.size || '' });

  const betIdData = getBetIdentityData(bet);

  const onSuccessPlacement = (placedBets: TCurrentBet[]) => {
    if (placedBets.length) {
      dispatch(successInlinePlacedBet({ ...bet, action: InlinePlacementActions.PLACED, placedBet: placedBets[0] }));

      if (!isOperatorBalanceEnabled && (!generalWsEnabled || !balanceWsEnabled)) {
        dispatch(fetchBalance());
      }
    }
  };

  const onErrorPlacement = (error: TPlacementError | string = '') => {
    dispatch(
      failureInlinePlacedBet({
        ...bet,
        action: bet.placedBet ? InlinePlacementActions.EDIT : InlinePlacementActions.SELECT,
        error
      })
    );

    if (isResponsibleGamblingError(error)) {
      dispatch(setRGErrorMessage(error));
    }
  };

  const onCancelledPlacement = () => {
    if (isPNCEnabled) {
      dispatch(
        updateInlineSelectedBet({
          ...betIdData,
          isTakeOffer: true,
          action: InlinePlacementActions.SELECT,
          validationMessage: {
            text: isLineMarket
              ? t(VALIDATION_ERROR_BET_OUTDATED_LINE, { unit: marketUnitTranslated })
              : t(VALIDATION_ERROR_BET_OUTDATED_ODDS),
            field: 'price',
            code: isLineMarket ? PlacementErrorsIds.BET_OUTDATED_LINE : PlacementErrorsIds.BET_OUTDATED_ODDS
          }
        })
      );
    }
  };

  const { editBetsHandler, cancelBetsHandler, placeBetsHandler } = usePlacementData({
    successPlacement: onSuccessPlacement,
    errorPlacement: onErrorPlacement,
    onCancelledPlacement,
    isInlinePlacement: true
  });

  useEffect(() => {
    if (
      bet.action &&
      (
        [
          InlinePlacementActions.PLACE,
          InlinePlacementActions.UPDATE,
          InlinePlacementActions.TAKE,
          InlinePlacementActions.CANCEL
        ] as InlinePlacementAction[]
      ).includes(bet.action)
    ) {
      dispatch(
        updateInlineSelectedBet({
          ...betIdData,
          action: InlinePlacementActions.PROGRESS,
          progressType: bet.action,
          ...(bet.action === InlinePlacementActions.TAKE ? { price: takeOdds } : {})
        })
      );
    }

    if (bet.action === InlinePlacementActions.PLACE) {
      placeBetsHandler({
        bets: [{ ...bet, side: bet.type.toUpperCase() }],
        options: {
          isTakeOffer: !!bet.isTakeOffer
        }
      });
    } else if (bet.placedBet) {
      if (bet.action === InlinePlacementActions.UPDATE) {
        editBetsHandler({
          marketId: bet.marketId,
          bets: [{ ...bet.placedBet, betUuid: bet.betUuid, price: bet.price, size: bet.size }]
        });
      } else if (bet.action === InlinePlacementActions.TAKE) {
        editBetsHandler({
          marketId: bet.marketId,
          bets: [{ ...bet.placedBet, betUuid: bet.betUuid, price: takeOdds, size: bet.size }]
        });
      } else if (bet.action === InlinePlacementActions.CANCEL) {
        cancelBetsHandler({ marketId: bet.marketId, bets: [{ ...bet.placedBet, betUuid: bet.betUuid }] });
      }
    }

    if (bet.action === InlinePlacementActions.PROGRESS) {
      dispatch(removePlacedSelectedBetError({ betUuid: bet.betUuid ?? '' }));
    }

    dispatch(hideInlinePlacementWhatIf(bet.action === InlinePlacementActions.PLACED ? false : null));
  }, [bet.action]);

  return null;
};

export default InlinePlacementInjectionItem;
